import { useEffect, useRef, useState } from 'react';
import Icon from './Icon';

/**
 * THE HELP COACH-MARK — the "here's how" card shown after a "how do I…" answer
 * drills to a screen. Placed WHERE THE EYE LANDS, not pinned to the window's
 * bottom edge (Vinnie, 2 Oct: the bottom card "is hard to see").
 *
 * It docks two ways (design-lead mock, Vinnie's pick — anchored, firm ring):
 *   - ANCHORED: when the destination marks a control with `.just-revealed`, the
 *     card sits just beneath that control with a caret pointing at it, and the
 *     control wears a firm brand ring (`.coach-ring`) for as long as the card is
 *     up — "what to do" and "where it is" as one shape (reuses the InfoTip caret
 *     idiom, R9). If it would fall below the fold it flips above the control.
 *   - TOP-OF-VIEW: otherwise (no revealed control on the landing screen — e.g. a
 *     tip whose control lives inside a card you open), the card sits at the top
 *     of the content area under the heading, where the eye arrives after a jump.
 *
 * It measures after a short beat so the destination view has mounted and run its
 * own reveal effect, and it re-measures on scroll/resize while anchored so the
 * card tracks the control. Dismiss: ×, Escape (App's handler), or another jump.
 */
const GAP = 10;
const MARGIN = 14;
const FALLBACK_W = 440;

type Pos = { top: number; left: number; caret: number | null; above: boolean };

export default function CoachMark({
  text,
  onClose,
  anchor,
  title = 'Here’s how',
}: {
  text: string;
  onClose: () => void;
  /** An explicit CSS selector to anchor to (e.g. the Help nav row for the
   *  first-run pointer). When omitted, the card finds the screen's own
   *  `.just-revealed` control, falling back to the top of the view. */
  anchor?: string;
  title?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const ringed = useRef<HTMLElement | null>(null);
  const [pos, setPos] = useState<Pos | null>(null);

  useEffect(() => {
    let raf = 0;
    const place = () => {
      const card = ref.current;
      const cardW = card?.offsetWidth || FALLBACK_W;
      const cardH = card?.offsetHeight || 64;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // The control the destination flashed, if any. `.just-revealed` is
      // transient (the screens drop it after ~1.8s), so once we've found it we
      // keep our own `.coach-ring` on it and track that on re-measure.
      const target =
        (ringed.current as HTMLElement | null) ||
        (anchor
          ? (document.querySelector(anchor) as HTMLElement | null)
          : (document.querySelector('.just-revealed') as HTMLElement | null));
      if (target && document.body.contains(target)) {
        if (ringed.current !== target) {
          if (ringed.current) ringed.current.classList.remove('coach-ring', 'coach-ring--text');
          target.classList.add('coach-ring');
          // A field or button carries its own box, so the ring hugs that box. A
          // word, label or heading is a text run — often a full-width block — so
          // we let the ring shrink to the text itself, never the whole row
          // (Vinnie, 2 Oct: highlight the word, or the field, nothing wider).
          const isControl = target.matches(
            'input, textarea, select, button, a, [role="button"], .btn, .input'
          );
          if (!isControl) target.classList.add('coach-ring--text');
          ringed.current = target;
        }
        const r = target.getBoundingClientRect();
        const left = Math.min(Math.max(r.left, MARGIN), vw - cardW - MARGIN);
        const caret = Math.min(Math.max(r.left + 20 - left, 16), cardW - 24);
        let top = r.bottom + GAP;
        let above = false;
        if (top + cardH > vh - MARGIN) {
          // No room below (e.g. the Help row at the sidebar foot) — sit above the
          // control with the caret pointing down at it.
          above = true;
          top = Math.max(r.top - cardH - GAP, MARGIN);
        }
        setPos({ top, left, caret, above });
      } else {
        // Top of the content area, under the heading.
        const view = document.querySelector('.view') as HTMLElement | null;
        const r = view?.getBoundingClientRect();
        const left = Math.min((r?.left ?? MARGIN) + 20, vw - cardW - MARGIN);
        const top = (r?.top ?? 64) + 14;
        setPos({ top, left, caret: null, above: false });
      }
    };
    const beat = window.setTimeout(() => {
      raf = requestAnimationFrame(place);
    }, 150);
    const onMove = () => {
      if (ringed.current) raf = requestAnimationFrame(place);
    };
    // A control on a data-loaded screen can mount AFTER our first measure, so we
    // sit in top-of-view. When the reveal finally flashes it, App fires
    // `coach:anchor` — re-measure then so we move onto the control instead of
    // staying top-of-view (ux-witness, 2 Oct). Only real `[data-reveal]` ids fire
    // this, so bespoke-reveal screens (Plan's About) are unaffected.
    const onAnchor = () => {
      // Place synchronously, not via rAF — the flashed `.just-revealed` is live
      // right now, and a deferred frame can land after it clears.
      if (!ringed.current) place();
    };
    window.addEventListener('coach:anchor', onAnchor);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.clearTimeout(beat);
      cancelAnimationFrame(raf);
      window.removeEventListener('coach:anchor', onAnchor);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
      if (ringed.current) ringed.current.classList.remove('coach-ring', 'coach-ring--text');
    };
  }, [text, anchor]);

  return (
    <div
      ref={ref}
      className={`coach-mark ${pos ? 'is-shown' : ''} ${pos?.caret != null ? 'coach-mark--anchored' : 'coach-mark--top'}`}
      role="status"
      aria-live="polite"
      style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: MARGIN }}
    >
      {pos?.caret != null && (
        <span
          className={`coach-mark-caret ${pos.above ? 'coach-mark-caret--down' : ''}`}
          style={{ left: pos.caret }}
          aria-hidden="true"
        />
      )}
      <span className="coach-mark-icon">
        <Icon name="help" size={16} />
      </span>
      <div className="coach-mark-body">
        <div className="coach-mark-title">{title}</div>
        <div className="coach-mark-text">{text}</div>
        <div className="coach-mark-foot">Press Esc to dismiss</div>
      </div>
      <button className="coach-mark-close" onClick={onClose} aria-label="Dismiss">
        <Icon name="close" size={14} />
      </button>
    </div>
  );
}
